/** API error → message (ADR-06 §2.3): `error.<CODE>` with Arabic fallback through `translateError`. */
import { translateError, type Translate } from "@faylasuf/i18n";
import { isApiClientError } from "../api/client.js";
import { traceRef } from "./telemetry.js";

/** [C28] D-259: unexpected failures carry the short trace reference so staff can find them in the console. */
const UNEXPECTED = new Set(["INTERNAL", "NETWORK", "UNKNOWN", "TENANT_MAINTENANCE"]);

export function errorMessage(t: Translate, e: unknown): string {
  if (isApiClientError(e)) {
    // ENGINE_VALIDATION carries the engine's own codes (C18): the first one is the message the operator needs.
    if (e.code === "ENGINE_VALIDATION") {
      const first = engineErrors(e)[0];
      if (first) return translateError(t, first.code, first.params);
    }
    const params: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(e.params)) if (typeof v === "string" || typeof v === "number") params[k] = v;
    const msg = translateError(t, e.code, params);
    return UNEXPECTED.has(e.code) || e.status >= 500 ? `${msg} — ${t("error.trace_ref", { id: traceRef(e.traceId) })}` : msg;
  }
  return `${translateError(t, "UNKNOWN", { code: e instanceof Error ? e.name : "?" })} — ${t("error.trace_ref", { id: traceRef() })}`;
}

/** A 401 means the session is gone: the shell returns to the login screen (ADR-03 §2.3). */
export function isSessionLost(e: unknown): boolean {
  return isApiClientError(e) && (e.status === 401 || e.code === "SESSION_REQUIRED" || e.code === "SESSION_EXPIRED" || e.code === "SESSION_GRACE_ENDED");
}

export interface EngineErrorView {
  readonly code: string;
  readonly params: Record<string, string | number>;
}

/** The engine error list of an `ENGINE_VALIDATION` refusal (`params.errors`), params flattened to strings. */
export function engineErrors(e: unknown): EngineErrorView[] {
  if (!isApiClientError(e) || e.code !== "ENGINE_VALIDATION") return [];
  const list = e.params["errors"];
  if (!Array.isArray(list)) return [];
  return list.map((x) => {
    const item = x as { code?: string; params?: Record<string, unknown> };
    const params: Record<string, string | number> = {};
    for (const [k, v] of Object.entries(item.params ?? {})) if (typeof v === "string" || typeof v === "number") params[k] = v;
    return { code: item.code ?? "UNKNOWN", params };
  });
}
